import React, { useState } from 'react';
import { DocumentType } from '../../../types/Document';
import { createUploadFormData } from '../../../utils/documentUtils';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import { useEnhancedAuth } from '../../../contexts/EnhancedAuthContext';
import styles from '../../../styles/DocumentManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';

interface DocumentUploadFormProps {
  type: DocumentType;
  onSuccess: () => void;
  onCancel: () => void;
  onUpload: (formData: FormData) => Promise<{ success: boolean; error?: string }>;
  isCompanyMode?: boolean;
}

const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({
  type,
  onSuccess,
  onCancel,
  onUpload,
  isCompanyMode = false,
}) => {
  const { selectedUser, agency } = useDocumentContext();
  const { userDetails: currentUser } = useEnhancedAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);
    setError('');

    try {
      if (!file) {
        setError('Please select a file.');
        return;
      }

      if (type.canExpire && !expiryDate) {
        setError('Please select an expiry date.');
        return;
      }

      // For company documents, use current user's ID (the manager uploading)
      // For user documents, use selected user's ID (the user whose documents we're managing)
      const userForUpload = isCompanyMode ? currentUser : selectedUser;

      const formData = createUploadFormData(
        file,
        title,
        description,
        expiryDate,
        type,
        userForUpload,
        agency
      );

      const result = await onUpload(formData);
      
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.docUploadForm}>
      <div className={styles.docUploadRow}>
        <label className={styles.docUploadLabel}>Title:</label>
        <input
          className={styles.docUploadInput}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          disabled={processing}
        />
      </div>

      <div className={styles.docUploadRow}>
        <label className={styles.docUploadLabel}>Description:</label>
        <input
          className={styles.docUploadInput}
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          disabled={processing}
        />
      </div>

      {type.recurrence !== 'none' && (
        <div className={styles.docUploadRow}>
          <label className={styles.docUploadLabel}>Expiry Date:</label>
          <input
            className={styles.docUploadInput}
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            required
            disabled={processing}
          />
        </div>
      )}

      <div className={styles.docUploadRow}>
        <div className={styles.docFileUploadContainer}>
          {file ? (
            <div className={`${styles.docCardFileInfo} ${styles.flexOne}`}>
              <div className={styles.docCardFileName}><strong>{file.name}</strong></div>
              <div className={styles.docCardFileSize}>Size: {((file.size / 1024).toFixed(2))} KB</div>
              <div className={styles.docCardReady}>Ready to upload</div>
              <button
                type="button"
                onClick={() => setFile(null)}
                className={`${buttonStyles.button} ${buttonStyles.remove}`}
                disabled={processing}
              >
                Remove
              </button>
            </div>
          ) : (
            <input
              className={styles.docUploadFileInput}
              type="file"
              onChange={e => setFile(e.target.files ? e.target.files[0] : null)}
              required
              disabled={processing}
            />
          )}
        </div>
      </div>

      {error && <div className={styles.formErrorCenter}>{error}</div>}

      <div className={styles.flexRowGap8}>
        <button
          type="submit"
          disabled={processing}
          className={buttonStyles.button + ' ' + buttonStyles.primary}
        >
          {processing ? 'Uploading...' : 'Submit'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className={buttonStyles.button + ' ' + buttonStyles.secondary}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default DocumentUploadForm;
